import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import PageLayout from '../../components/PageLayout';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAuth } from '../../context/AuthContext';
import { apiClient } from '../../services/api';

type Policy = {
  enabled: boolean;
  provider: 'openai' | 'openai_compatible';
  model: string;
  maxTurns: number;
  maxSteps: number;
  maxOutputTokens: number;
  sessionMinutes: number;
};
type Config = {
  policy: Policy & { updated_at?: string };
  readiness: { available: boolean; reason: string };
};
function PolicyEditor({ config }: { config: Config }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Policy>(() => ({
    enabled: config.policy.enabled,
    provider: config.policy.provider,
    model: config.policy.model,
    maxTurns: config.policy.maxTurns,
    maxSteps: config.policy.maxSteps,
    maxOutputTokens: config.policy.maxOutputTokens,
    sessionMinutes: config.policy.sessionMinutes,
  }));
  const mutation = useMutation(
    (patch: Policy) => apiClient.patch('/platform/booking-assistant', patch),
    {
      onSuccess: () => {
        toast.success('Booking assistant settings saved');
        queryClient.invalidateQueries('booking-assistant-policy');
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.message ||
            'Unable to save booking assistant settings'
        );
      },
    }
  );
  return (
    <form
      className="card p-6 space-y-6"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate(draft);
      }}
    >
      <div
        className="rounded-card bg-surface-container-low p-4 text-sm text-on-surface-variant"
        role="status"
      >
        {config.readiness.available
          ? 'Provider configuration is present. A live conversation still needs to be verified.'
          : config.readiness.reason === 'disabled'
          ? 'The booking assistant is globally disabled.'
          : 'Provider setup is required before live conversations are available.'}
      </div>
      <label className="flex gap-3 items-center text-sm font-medium">
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(event) =>
            setDraft({ ...draft, enabled: event.target.checked })
          }
        />{' '}
        Enable the booking assistant across opted-in tenants
      </label>
      <div className="grid gap-6 md:grid-cols-2">
        <label className="space-y-2 text-sm font-medium">
          Provider
          <select
            className="input-field w-full"
            value={draft.provider}
            onChange={(event) =>
              setDraft({
                ...draft,
                provider: event.target.value as Policy['provider'],
              })
            }
          >
            <option value="openai">OpenAI</option>
            <option value="openai_compatible">
              OpenAI-compatible provider
            </option>
          </select>
        </label>
        <label className="space-y-2 text-sm font-medium">
          Model
          <input
            className="input-field w-full"
            maxLength={120}
            value={draft.model}
            onChange={(event) =>
              setDraft({ ...draft, model: event.target.value })
            }
            placeholder="Enter your provider's model identifier"
          />
        </label>
        {(
          [
            ['maxTurns', 'Maximum response attempts per session', 2, 50],
            ['maxSteps', 'Maximum tool steps per response', 1, 8],
            [
              'maxOutputTokens',
              'Maximum output tokens per response',
              256,
              4000,
            ],
            ['sessionMinutes', 'Session lifetime in minutes', 15, 1440],
          ] as const
        ).map(([field, label, min, max]) => (
          <label className="space-y-2 text-sm font-medium" key={field}>
            {label}
            <input
              className="input-field w-full"
              type="number"
              min={min}
              max={max}
              required
              value={draft[field]}
              onChange={(event) =>
                setDraft({ ...draft, [field]: Number(event.target.value) })
              }
            />
          </label>
        ))}
      </div>
      <p className="text-sm text-on-surface-variant">
        Configure{' '}
        {draft.provider === 'openai'
          ? 'OPENAI_API_KEY'
          : 'BOOKING_ASSISTANT_COMPATIBLE_API_KEY and BOOKING_ASSISTANT_COMPATIBLE_BASE_URL'}{' '}
        privately on the backend. Credentials are never stored in this page.
        Tenants separately enable chat in their inquiry form settings.
      </p>
      <p className="text-sm text-on-surface-variant">
        The assistant gathers transportation plans and prepares a request for
        customer review. Operator approval remains part of quoting, inventory
        confirmation and partner sourcing.
      </p>
      <button
        className="btn-primary"
        disabled={mutation.isLoading}
        type="submit"
      >
        {mutation.isLoading ? 'Saving…' : 'Save settings'}
      </button>
    </form>
  );
}
export default function BookingAssistantSettings() {
  const { user } = useAuth();
  const allowed = !!user?.is_platform_admin && !user?.is_support_staff;
  const query = useQuery<Config>(
    'booking-assistant-policy',
    async () => (await apiClient.get('/platform/booking-assistant')).data,
    { enabled: allowed }
  );
  return (
    <PageLayout width="content">
      <div className="max-w-3xl">
        <h1 className="text-3xl font-semibold text-on-surface">
          Booking assistant
        </h1>
        <p className="mt-2 mb-8 text-on-surface-variant">
          One provider configuration for customer conversations across your
          platform.
        </p>
        {!allowed ? (
          <p>Platform administrator access is required.</p>
        ) : query.isLoading ? (
          <LoadingSpinner />
        ) : query.isError ? (
          <div role="alert">
            Unable to load booking assistant settings.{' '}
            <button className="btn-secondary" onClick={() => query.refetch()}>
              Retry
            </button>
          </div>
        ) : query.data ? (
          <PolicyEditor
            key={query.data.policy.updated_at || 'initial'}
            config={query.data}
          />
        ) : null}
      </div>
    </PageLayout>
  );
}
